/**
 * API Configuration - COMPREHENSIVE VERSION
 * 
 * Central configuration for ALL API endpoints and settings.
 * Supports: Products, Inventory, Sales, Orders, Customers, Analytics, and all dashboard features.
 * Use environment variables for different environments (dev, staging, production).
 */

// API Base URL - reads from environment variable or defaults to localhost
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// API Version (disabled - backend doesn't use versioning yet)
export const API_VERSION = '';

// Full API URL (no version prefix for now)
export const API_URL = API_BASE_URL;

// API Endpoints - COMPREHENSIVE (Tenant/Branch Architecture)
export const API_ENDPOINTS = {
  // ============================================
  // AUTHENTICATION & AUTHORIZATION
  // ============================================
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REGISTER: '/auth/register',
    REFRESH_TOKEN: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    CHANGE_PASSWORD: '/auth/change-password',
    ME: '/auth/me', // Get current user with tenant/branch
    CONTEXT: '/auth/context', // Get user + tenant + branches + active branch atomically
    UPDATE_PROFILE: '/auth/profile',
  },

  // ============================================
  // TENANT & BRANCH MANAGEMENT
  // ============================================
  TENANTS: {
    GET: '/tenants/me',
    UPDATE: '/tenants/me',
    SETTINGS: '/tenants/me/settings',
    BRANDING: '/tenants/me/branding',
  },

  BRANCHES: {
    LIST: '/branches',
    GET: (id: string) => `/branches/${id}`,
    CREATE: '/branches',
    UPDATE: (id: string) => `/branches/${id}`,
    DELETE: (id: string) => `/branches/${id}`,
    SWITCH: (id: string) => `/branches/${id}/switch`,
    STATS: (id: string) => `/branches/${id}/stats`,
  },

  // ============================================
  // USER MANAGEMENT (RBAC)
  // ============================================
  USERS: {
    LIST: '/users',
    GET: (id: string) => `/users/${id}`,
    CREATE: '/users',
    UPDATE: (id: string) => `/users/${id}`,
    DELETE: (id: string) => `/users/${id}`,
    UPDATE_ROLE: (id: string) => `/users/${id}/role`,
    ACTIVATE: (id: string) => `/users/${id}/activate`,
    DEACTIVATE: (id: string) => `/users/${id}/deactivate`,
  },

  RBAC: {
    PERMISSIONS: '/rbac/permissions',
    ROLES: '/rbac/roles',
    ROLE_PERMISSIONS: (roleId: string) => `/rbac/roles/${roleId}/permissions`,
    USER_ROLES: (userId: string) => `/rbac/users/${userId}/roles`,
    USER_PERMISSIONS: (userId: string) => `/rbac/users/${userId}/permissions`,
    MY_PERMISSIONS: '/rbac/me/permissions',
  },

  // ============================================
  // CUSTOMERS & MEMBERS
  // ============================================
  CUSTOMERS: {
    LIST: '/customers',
    GET: (id: string) => `/customers/${id}`,
    CREATE: '/customers',
    UPDATE: (id: string) => `/customers/${id}`,
    DELETE: (id: string) => `/customers/${id}`,
    SEARCH: '/customers/search',
    STATS: '/customers/stats',
  },

  // ============================================
  // ORDERS & ORDER MANAGEMENT
  // ============================================
  ORDERS: {
    LIST: '/orders',
    GET: (id: string) => `/orders/${id}`,
    CREATE: '/orders',
    UPDATE: (id: string) => `/orders/${id}`,
    DELETE: (id: string) => `/orders/${id}`,
    UPDATE_STATUS: (id: string) => `/orders/${id}/status`,
    CANCEL: (id: string) => `/orders/${id}/cancel`,
    STATS: '/orders/stats',
    INVOICE: (id: string) => `/orders/${id}/invoice`,
    RECEIPT: (id: string) => `/orders/${id}/receipt`,
  },

  // ============================================
  // PRODUCTS & MENU ITEMS
  // ============================================
  CATEGORIES: {
    LIST: '/categories',
    GET: (id: string) => `/categories/${id}`,
    CREATE: '/categories',
    UPDATE: (id: string) => `/categories/${id}`,
    DELETE: (id: string) => `/categories/${id}`,
    REORDER: '/categories/reorder',
    TOGGLE_ACTIVE: (id: string) => `/categories/${id}/toggle-active`,
    STATS: (id: string) => `/categories/${id}/stats`,
  },

  PRODUCTS: {
    LIST: '/products',
    GET: (id: string) => `/products/${id}`,
    CREATE: '/products',
    UPDATE: (id: string) => `/products/${id}`,
    DELETE: (id: string) => `/products/${id}`,
    BULK_UPDATE: '/products/bulk-update',
    BULK_DELETE: '/products/bulk-delete',
    BY_CATEGORY: (categoryId: string) => `/products/category/${categoryId}`,
    SEARCH: '/products/search',
    POPULAR: '/products/popular',
    LOW_STOCK: '/products/low-stock',
    OUT_OF_STOCK: '/products/out-of-stock',
    TOGGLE_AVAILABILITY: (id: string) => `/products/${id}/toggle-availability`,
    UPLOAD_IMAGE: (id: string) => `/products/${id}/image`,
    DELETE_IMAGE: (id: string, imageId: string) => `/products/${id}/images/${imageId}`,
    VARIANTS: (id: string) => `/products/${id}/variants`,
    ADD_VARIANT: (id: string) => `/products/${id}/variants`,
    UPDATE_VARIANT: (id: string, variantId: string) => `/products/${id}/variants/${variantId}`,
    DELETE_VARIANT: (id: string, variantId: string) => `/products/${id}/variants/${variantId}`,
  },

  // Alias for backward compatibility
  MENU_ITEMS: {
    LIST: '/products',
    GET: (id: string) => `/products/${id}`,
    CREATE: '/products',
    UPDATE: (id: string) => `/products/${id}`,
    DELETE: (id: string) => `/products/${id}`,
    BY_CATEGORY: (categoryId: string) => `/products/category/${categoryId}`,
  },

  // ============================================
  // INVENTORY MANAGEMENT
  // ============================================
  INVENTORY: {
    LIST: '/inventory',
    GET: (id: string) => `/inventory/${id}`,
    CREATE: '/inventory',
    UPDATE: (id: string) => `/inventory/${id}`,
    DELETE: (id: string) => `/inventory/${id}`,
    ADJUST_STOCK: (id: string) => `/inventory/${id}/adjust`,
    BULK_ADJUST: '/inventory/bulk-adjust',
    LOW_STOCK: '/inventory/low-stock',
    OUT_OF_STOCK: '/inventory/out-of-stock',
    STOCK_HISTORY: (id: string) => `/inventory/${id}/history`,
    STOCK_ALERTS: '/inventory/alerts',
    STATS: '/inventory/stats',
    VALUATION: '/inventory/valuation',
    WASTE: '/inventory/waste',
    ADD_WASTE: '/inventory/waste',
  },

  STOCK_MOVEMENTS: {
    LIST: '/stock-movements',
    GET: (id: string) => `/stock-movements/${id}`,
    CREATE: '/stock-movements',
    BY_PRODUCT: (productId: string) => `/stock-movements/product/${productId}`,
    BY_TYPE: (type: string) => `/stock-movements/type/${type}`,
  },

  // ============================================
  // SUPPLIERS & PURCHASE ORDERS
  // ============================================
  SUPPLIERS: {
    LIST: '/suppliers',
    GET: (id: string) => `/suppliers/${id}`,
    CREATE: '/suppliers',
    UPDATE: (id: string) => `/suppliers/${id}`,
    DELETE: (id: string) => `/suppliers/${id}`,
    PRODUCTS: (id: string) => `/suppliers/${id}/products`,
    ORDERS: (id: string) => `/suppliers/${id}/orders`,
    STATS: (id: string) => `/suppliers/${id}/stats`,
  },

  PURCHASE_ORDERS: {
    LIST: '/purchase-orders',
    GET: (id: string) => `/purchase-orders/${id}`,
    CREATE: '/purchase-orders',
    UPDATE: (id: string) => `/purchase-orders/${id}`,
    DELETE: (id: string) => `/purchase-orders/${id}`,
    APPROVE: (id: string) => `/purchase-orders/${id}/approve`,
    RECEIVE: (id: string) => `/purchase-orders/${id}/receive`,
    CANCEL: (id: string) => `/purchase-orders/${id}/cancel`,
    BY_SUPPLIER: (supplierId: string) => `/purchase-orders/supplier/${supplierId}`,
    PENDING: '/purchase-orders/pending',
    STATS: '/purchase-orders/stats',
  },

  // ============================================
  // SALES MANAGEMENT
  // ============================================
  SALES: {
    SUMMARY: '/sales/summary',
    BY_DATE: '/sales/by-date',
    BY_PRODUCT: '/sales/by-product',
    BY_CATEGORY: '/sales/by-category',
    BY_CUSTOMER: '/sales/by-customer',
    TOP_PRODUCTS: '/sales/top-products',
    TOP_CUSTOMERS: '/sales/top-customers',
    TRENDS: '/sales/trends',
    FORECAST: '/sales/forecast',
    COMPARE: '/sales/compare',
    STATS: '/sales/stats',
    DAILY: '/sales/daily',
    WEEKLY: '/sales/weekly',
    MONTHLY: '/sales/monthly',
    YEARLY: '/sales/yearly',
  },

  // ============================================
  // PAYMENTS & INVOICES
  // ============================================
  PAYMENTS: {
    LIST: '/payments',
    GET: (id: string) => `/payments/${id}`,
    CREATE: '/payments',
    PROCESS: (id: string) => `/payments/${id}/process`,
    REFUND: (id: string) => `/payments/${id}/refund`,
    BY_ORDER: (orderId: string) => `/payments/order/${orderId}`,
    BY_METHOD: (method: string) => `/payments/method/${method}`,
    STATS: '/payments/stats',
  },

  INVOICES: {
    LIST: '/invoices',
    GET: (id: string) => `/invoices/${id}`,
    CREATE: '/invoices',
    UPDATE: (id: string) => `/invoices/${id}`,
    DELETE: (id: string) => `/invoices/${id}`,
    SEND: (id: string) => `/invoices/${id}/send`,
    DOWNLOAD: (id: string) => `/invoices/${id}/download`,
    BY_CUSTOMER: (customerId: string) => `/invoices/customer/${customerId}`,
    PENDING: '/invoices/pending',
    OVERDUE: '/invoices/overdue',
    STATS: '/invoices/stats',
  },

  // ============================================
  // REVIEWS & RATINGS
  // ============================================
  REVIEWS: {
    LIST: '/reviews',
    GET: (id: string) => `/reviews/${id}`,
    CREATE: '/reviews',
    UPDATE: (id: string) => `/reviews/${id}`,
    DELETE: (id: string) => `/reviews/${id}`,
    REPLY: (id: string) => `/reviews/${id}/reply`,
    APPROVE: (id: string) => `/reviews/${id}/approve`,
    REJECT: (id: string) => `/reviews/${id}/reject`,
    BY_PRODUCT: (productId: string) => `/reviews/product/${productId}`,
    BY_CUSTOMER: (customerId: string) => `/reviews/customer/${customerId}`,
    PENDING: '/reviews/pending',
    STATS: '/reviews/stats',
  },

  // ============================================
  // ANALYTICS & REPORTS
  // ============================================
  ANALYTICS: {
    DASHBOARD: '/analytics/dashboard',
    REVENUE: '/analytics/revenue',
    SALES: '/analytics/sales',
    CUSTOMERS: '/analytics/customers',
    PRODUCTS: '/analytics/products',
    ORDERS: '/analytics/orders',
    INVENTORY: '/analytics/inventory',
    PERFORMANCE: '/analytics/performance',
    TRENDS: '/analytics/trends',
    COMPARISON: '/analytics/comparison',
  },

  REPORTS: {
    SALES: '/reports/sales',
    INVENTORY: '/reports/inventory',
    CUSTOMERS: '/reports/customers',
    PRODUCTS: '/reports/products',
    REVENUE: '/reports/revenue',
    PROFIT_LOSS: '/reports/profit-loss',
    TAX: '/reports/tax',
    CUSTOM: '/reports/custom',
    EXPORT: (type: string) => `/reports/export/${type}`,
    SCHEDULE: '/reports/schedule',
    SCHEDULED: '/reports/scheduled',
  },

  // ============================================
  // PROMOTIONS & DISCOUNTS
  // ============================================
  PROMOTIONS: {
    LIST: '/promotions',
    GET: (id: string) => `/promotions/${id}`,
    CREATE: '/promotions',
    UPDATE: (id: string) => `/promotions/${id}`,
    DELETE: (id: string) => `/promotions/${id}`,
    ACTIVATE: (id: string) => `/promotions/${id}/activate`,
    DEACTIVATE: (id: string) => `/promotions/${id}/deactivate`,
    ACTIVE: '/promotions/active',
    UPCOMING: '/promotions/upcoming',
    EXPIRED: '/promotions/expired',
    STATS: (id: string) => `/promotions/${id}/stats`,
  },

  COUPONS: {
    LIST: '/coupons',
    GET: (id: string) => `/coupons/${id}`,
    CREATE: '/coupons',
    UPDATE: (id: string) => `/coupons/${id}`,
    DELETE: (id: string) => `/coupons/${id}`,
    VALIDATE: '/coupons/validate',
    APPLY: '/coupons/apply',
    USAGE: (id: string) => `/coupons/${id}/usage`,
    STATS: '/coupons/stats',
  },

  // ============================================
  // NOTIFICATIONS
  // ============================================
  NOTIFICATIONS: {
    LIST: '/notifications',
    GET: (id: string) => `/notifications/${id}`,
    CREATE: '/notifications',
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    DELETE: (id: string) => `/notifications/${id}`,
    UNREAD_COUNT: '/notifications/unread-count',
    PREFERENCES: '/notifications/preferences',
    UPDATE_PREFERENCES: '/notifications/preferences',
  },

  // ============================================
  // TASKS & TODO
  // ============================================
  TASKS: {
    LIST: '/tasks',
    GET: (id: string) => `/tasks/${id}`,
    CREATE: '/tasks',
    UPDATE: (id: string) => `/tasks/${id}`,
    DELETE: (id: string) => `/tasks/${id}`,
    COMPLETE: (id: string) => `/tasks/${id}/complete`,
    ASSIGN: (id: string) => `/tasks/${id}/assign`,
    BY_USER: (userId: string) => `/tasks/user/${userId}`,
    BY_STATUS: (status: string) => `/tasks/status/${status}`,
    STATS: '/tasks/stats',
  },

  // ============================================
  // SETTINGS & CONFIGURATION
  // ============================================
  SETTINGS: {
    GET: '/settings',
    UPDATE: '/settings',
    UPLOAD_LOGO: '/settings/logo',
    UPDATE_BRAND_COLORS: '/settings/brand-colors',
    RESET_BRAND_COLORS: '/settings/brand-colors/reset',
    TAX: '/settings/tax',
    CURRENCY: '/settings/currency',
    TIMEZONE: '/settings/timezone',
    EMAIL: '/settings/email',
    SMS: '/settings/sms',
    PAYMENT_METHODS: '/settings/payment-methods',
    SHIPPING: '/settings/shipping',
    BACKUP: '/settings/backup',
    RESTORE: '/settings/restore',
  },

  // ============================================
  // HELP & SUPPORT
  // ============================================
  HELP: {
    FAQS: '/help/faqs',
    GET_FAQ: (id: string) => `/help/faqs/${id}`,
    CREATE_FAQ: '/help/faqs',
    UPDATE_FAQ: (id: string) => `/help/faqs/${id}`,
    DELETE_FAQ: (id: string) => `/help/faqs/${id}`,
    SEARCH: '/help/search',
    SUPPORT_TICKET: '/help/support-ticket',
  },

  // ============================================
  // CART (if backend managed)
  // ============================================
  CART: {
    GET: '/cart',
    ADD_ITEM: '/cart/items',
    UPDATE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    REMOVE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    CLEAR: '/cart/clear',
    APPLY_COUPON: '/cart/coupon',
    REMOVE_COUPON: '/cart/coupon',
  },

  // ============================================
  // DASHBOARD DATA
  // ============================================
  DASHBOARD: {
    STATS: '/dashboard/stats',
    RECENT_ORDERS: '/dashboard/recent-orders',
    RECENT_CUSTOMERS: '/dashboard/recent-customers',
    TOP_PRODUCTS: '/dashboard/top-products',
    REVENUE_CHART: '/dashboard/revenue-chart',
    SALES_CHART: '/dashboard/sales-chart',
    QUICK_ACTIONS: '/dashboard/quick-actions',
  },
} as const;

// Request timeout in milliseconds
export const REQUEST_TIMEOUT = 30000; // 30 seconds

// Token storage keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  THEME: 'theme',
  CART: 'cart',
} as const;

// HTTP Headers
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
} as const;

// API Response Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

// Export type for autocomplete
export type ApiEndpoints = typeof API_ENDPOINTS;